import { useContext, useEffect, useMemo } from "react";
import { AuthContext } from "contexts/AuthProvider";
import { CheckoutContext } from "contexts/CheckoutProvider";
import { normalizeOrderSummary } from "components/Membership/membershipUtil";
import { mergeWithContext } from "utils/checkout";
import { MembershipState } from "components/Membership/membershipTypes";
import { OrderSummary } from "lib/api/checkout";

export interface UseMembershipSessionArgs
  extends Pick<MembershipState, "checkoutSession" | "isAuthenticated"> {
  orderSummary: OrderSummary | null;
}

const useMembershipSession = ({
  checkoutSession: serverSideCheckoutSession,
  isAuthenticated: serverSideIsAuthenticated,
  orderSummary: serverSideOrderSummary,
}: UseMembershipSessionArgs) => {
  const {
    getCheckoutSession,
    getOrderSummary,
    orderSummary: ctxOrderSummary,
    session: ctxCheckoutSession,
  } = useContext(CheckoutContext);
  const { isAuthenticated: ctxIsAuthenticated } = useContext(AuthContext);

  useEffect(() => {
    if (!serverSideCheckoutSession && !ctxCheckoutSession) {
      getCheckoutSession?.();
    }
  }, [ctxCheckoutSession, getCheckoutSession, serverSideCheckoutSession]);

  useEffect(() => {
    if (
      !serverSideOrderSummary &&
      !ctxOrderSummary &&
      (serverSideCheckoutSession?.selectedPlan ||
        ctxCheckoutSession?.selectedPlan) &&
      (serverSideCheckoutSession?.accountInfo?.firstName ||
        ctxCheckoutSession?.accountInfo?.firstName)
    ) {
      getOrderSummary?.();
    }
  }, [
    ctxCheckoutSession,
    ctxOrderSummary,
    getOrderSummary,
    serverSideCheckoutSession,
    serverSideOrderSummary,
  ]);

  // mergeWithContext() allocates a new object every call; memoize so MembershipStepFlow’s
  // sync effect (deps on checkoutSession) does not setState on every render → infinite loop.
  const checkoutSession = useMemo(() => {
    if (!serverSideCheckoutSession && !ctxCheckoutSession) {
      return null;
    }
    return mergeWithContext(serverSideCheckoutSession, ctxCheckoutSession);
  }, [serverSideCheckoutSession, ctxCheckoutSession]);

  const isAuthenticated = serverSideIsAuthenticated || ctxIsAuthenticated;

  const orderSummarySource = useMemo(() => {
    if (!serverSideOrderSummary && !ctxOrderSummary) {
      return null;
    }
    return mergeWithContext(serverSideOrderSummary, ctxOrderSummary);
  }, [serverSideOrderSummary, ctxOrderSummary]);

  // normalizeOrderSummary() also returns a fresh object per call when data exists; stabilize for the same reason.
  const orderSummary = useMemo(
    () => normalizeOrderSummary(orderSummarySource),
    [orderSummarySource]
  );

  return {
    checkoutSession,
    isAuthenticated,
    orderSummary,
  };
};

export default useMembershipSession;
